'use client';

// Khối "Đặt phòng" trong modal chi tiết căn ở /sale (công khai, khách không đăng nhập).
// Luồng: chọn ngày → báo giá (server tính) → nhập thông tin → tạo booking nháp + hiện QR → poll tới khi tiền về.
// Mọi số tiền do SERVER tính; client chỉ hiển thị. Funnel (mở/chọn ngày/nhập form/bỏ dở) gửi qua /api/sale/track,
// các bước created/pay_shown/paid do server tự ghi (xem lib/sale-booking.ts).
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import qrcode from 'qrcode-generator';
import { useToast } from '@/components/Toast';
import { dateAddDays, fmtVND, getOttChatUrl, todayISO } from '@/lib/utils';
import { logPublicClientError } from '@/lib/client-error-logger';
import { useSaleLang } from '@/lib/sale-i18n';
import { clearSaleGuest, readSaleGuest, saveSaleGuest, storedStayDates } from '@/lib/sale-guest-info';
import StayDatePicker from './StayDatePicker';

export interface SaleBookingInfo {
  enabled: boolean; buildingEnabled?: Record<string, boolean>; payMode: 'payos_own' | 'payos_ledifa' | 'vietqr'; amountMode: 'full' | 'deposit';
  depositPercent: number; holdMinutes: number; discountEnabled: boolean; discountPercent: number; note?: string;
}
interface Quote {
  nights: { date: string; price: number }[]; nightCount: number; subtotal: number; discount: number; discountPercent: number; total: number; due: number;
  listTotal: number; savings: number; savingsPercent: number; // giá niêm yết Airbnb cho cùng các đêm (0 = không so sánh)
  extraGuests: number; extraGuestFee: number; extraGuestFeePerNight: number; maxAdults: number; maxGuests: number; // phụ thu người lớn vượt sức chứa (đã nằm trong total)
  payMode: 'payos_own' | 'payos_ledifa' | 'vietqr';  // cách nhận tiền THEO TOÀ của căn đang xem
  booking?: SaleBookingInfo;                         // cấu hình HIỆU LỰC của toà chứa căn (cọc/giữ chỗ/giảm giá/chính sách có thể khác mặc định chung)
}
interface Payment {
  mode: 'payos_own' | 'payos_ledifa' | 'vietqr'; amountDue: number; totalAmount: number; transferContent: string;
  checkoutUrl?: string; qrPayload?: string; qrImage?: string; bank?: { bankName?: string; accountNo?: string; accountName?: string }; expiresAtMs: number;
}
type Stage = 'closed' | 'dates' | 'form' | 'payment' | 'paid' | 'conflict' | 'held';

// Mã lỗi server → key từ điển `book.err.<mã>` (lib/sale-i18n-booking.ts). Mã lạ → rơi về thông báo chung.
const ERR_CODES = new Set(['unavailable', 'past_date', 'invalid_dates', 'booking_disabled', 'min_stay', 'max_stay', 'too_many_guests', 'no_price', 'rate_limited', 'invalid_guest', 'far_future', 'too_many_pending', 'duplicate_pending', 'busy']);
const errKey = (code: string | undefined, fallback: string) => (code && ERR_CODES.has(code) ? `book.err.${code}` : fallback);

/** Dựng chuỗi dịch có `**đậm**` và `{slot}` (node React, vd link điện thoại). */
function Rich({ text, slots }: Readonly<{ text: string; slots?: Record<string, ReactNode> }>): ReactNode {
  return text.split(/(\*\*.+?\*\*|\{\w+\})/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) return <b key={i}><Rich text={part.slice(2, -2)} slots={slots} /></b>;
    const m = /^\{(\w+)\}$/.exec(part);
    if (m && slots && m[1] in slots) return <Fragment key={i}>{slots[m[1]]}</Fragment>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}

type T = (key: string, vars?: Record<string, string | number>) => string;
/** Bản dịch của fmtHold (lib/sale-hold.ts chỉ ra tiếng Việt). */
function holdLabel(t: T, minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) return t('book.hold.hour', { n: minutes / 60 });
  if (minutes > 60) return t('book.hold.hourmin', { h: Math.floor(minutes / 60), m: minutes % 60 });
  return t('book.hold.min', { n: minutes });
}

/** Ảnh QR từ chuỗi VietQR — vẽ SVG ngay trên client, không phụ thuộc dịch vụ ngoài. */
function QrSvg({ value }: Readonly<{ value: string }>) {
  const { t } = useSaleLang();
  const svg = useMemo(() => {
    try {
      const q = qrcode(0, 'M');
      q.addData(value, 'Byte');
      q.make();
      const n = q.getModuleCount(), m = 2;
      let d = '';
      for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (q.isDark(r, c)) d += `M${c + m} ${r + m}h1v1h-1z`;
      return { d, size: n + m * 2 };
    } catch { return null; }
  }, [value]);
  if (!svg) return <div className="text-xs text-rose-600">{t('book.qr.fail')}</div>;
  return (
    <svg viewBox={`0 0 ${svg.size} ${svg.size}`} className="w-52 h-52 bg-white rounded-xl p-2 mx-auto" shapeRendering="crispEdges" role="img" aria-label={t('book.qr.alt')}>
      <path d={svg.d} fill="#000" />
    </svg>
  );
}

/** Khung giải thích thời gian giữ chỗ — dùng ở bước điền form và màn QR. `until` chỉ có sau khi đã tạo booking. */
function HoldNotice({ hold, until, contactPhone, transfer }: Readonly<{ hold: string; until?: string; contactPhone?: string | null; transfer: boolean }>) {
  const { t } = useSaleLang();
  return (
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs space-y-1 text-[#4a3d30]">
      <div className="font-semibold text-amber-800">{until ? t('book.hold.title_until', { hold, until }) : t('book.hold.title_now', { hold })}</div>
      <ul className="list-disc pl-4 space-y-0.5">
        <li><Rich text={t('book.hold.li_pay', { hold })} /></li>
        <li><Rich text={t('book.hold.li_expire', { hold })} /></li>
        {transfer
          ? <li>{t('book.hold.li_transfer')}</li>
          : <li>{t('book.hold.li_auto')}</li>}
        {until && <li><Rich text={t('book.hold.li_late')} /></li>}
      </ul>
      {until && contactPhone && (
        <div className="pt-1">{t('book.hold.contact')} <a className="underline font-semibold" href={`tel:${contactPhone.replace(/\s/g, '')}`}>{contactPhone}</a>
          {' · '}<a className="underline" href={getOttChatUrl('zalo', contactPhone)} target="_blank" rel="noreferrer">Zalo</a></div>
      )}
    </div>
  );
}

/** Nút "Gọi lại cho tôi": khách để lại SĐT → server báo nhóm Telegram Vận hành để NV gọi lại trao đổi về booking. */
function CallbackBox({ roomId, getSession, defaults, contactPhone }: Readonly<{
  roomId: string; getSession: () => string; contactPhone?: string | null;
  defaults: { phone: string; name: string; from: string; to: string; adults: number; bookingId?: string };
}>) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [state, setState] = useState<'idle' | 'sent' | 'unsent'>('idle');
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(''); // key từ điển, dịch lúc render để đổi ngôn ngữ giữa chừng vẫn đúng
  const { t } = useSaleLang();
  const lastSelfCall = useRef<Record<string, number>>({});
  const inputCls = 'w-full border border-[#e4ddd4] rounded-lg px-2 py-1.5 text-sm';

  // Khách CHỦ ĐỘNG gọi/copy SĐT tenant → báo nhóm Vận hành (nghe máy). Chống báo trùng: mỗi hành động 1 lần / 60s / phiên.
  const reportSelfCall = (action: 'call' | 'copy') => {
    const now = Date.now();
    if (now - (lastSelfCall.current[action] ?? 0) < 60_000) return;
    lastSelfCall.current[action] = now;
    fetch('/api/sale/self-call', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
      body: JSON.stringify({ roomId, action, phone: phone || defaults.phone, name: name || defaults.name, from: defaults.from, to: defaults.to, bookingId: defaults.bookingId, sessionId: getSession() }),
    }).catch(e => logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_self_call', message: e instanceof Error ? e.message : String(e) }));
  };
  const copyTenantPhone = () => {
    if (!contactPhone) return;
    reportSelfCall('copy');
    navigator.clipboard.writeText(contactPhone).then(
      () => setCopied(true),
      () => setErr('book.cb.copyfail'),
    );
  };

  const toggle = () => {
    if (!open) { const sv = readSaleGuest(); setPhone(p => p || defaults.phone || sv.phone || ''); setName(n => n || defaults.name || sv.name || ''); }
    setOpen(o => !o);
  };

  const send = async () => {
    if (sending) return;
    setSending(true); setErr('');
    try {
      const res = await fetch('/api/sale/callback', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, phone, name, from: defaults.from, to: defaults.to, adults: defaults.adults, bookingId: defaults.bookingId, sessionId: getSession() }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status >= 500 || !j.error) throw new Error(`callback ${res.status}`);
        setErr(j.error === 'invalid_phone' ? 'book.cb.err_phone' : j.error === 'rate_limited' ? 'book.cb.err_rate' : 'book.cb.err_fail');
        return;
      }
      saveSaleGuest({ phone, name }); // SĐT/tên vừa nhập ở đây cũng dùng lại cho form đặt phòng & các nơi khác
      setState(j.notified ? 'sent' : 'unsent');
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_callback', message: e instanceof Error ? e.message : String(e) });
      setErr('book.cb.err_net');
    } finally { setSending(false); }
  };

  if (state === 'sent') return <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800"><Rich text={t('book.cb.sent', { phone })} /></div>;
  if (state === 'unsent') {
    return (
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
        {t('book.cb.unsent')}{contactPhone ? <> {t('book.cb.call_direct')} <a className="underline font-semibold" href={`tel:${contactPhone.replace(/\s/g, '')}`}>{contactPhone}</a>.</> : ` ${t('book.cb.retry_later')}`}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button type="button" onClick={toggle} className="text-sm font-medium underline text-[#4a3d30]">{t('book.cb.btn')}</button>
        {contactPhone && (
          <span className="text-xs text-[#8a7a68] flex items-center gap-1.5">
            {t('book.cb.or_call')}
            <a href={`tel:${contactPhone.replace(/\s/g, '')}`} onClick={() => reportSelfCall('call')} className="font-semibold text-[#4a3d30] underline">{contactPhone}</a>
            <button type="button" onClick={copyTenantPhone} className="px-1.5 py-0.5 rounded border border-[#e4ddd4] text-[11px] text-[#4a3d30]">{copied ? t('book.copied_short') : t('book.copy_btn')}</button>
          </span>
        )}
      </div>
      {open && (
        <div className="rounded-lg border border-[#e4ddd4] p-3 space-y-2">
          <div className="text-xs text-[#8a7a68]">{t('book.cb.hint')}{phone && defaults.phone === phone ? t('book.cb.prefilled') : ''}</div>
          <input className={inputCls} inputMode="tel" placeholder={t('book.cb.ph_phone')} value={phone} onChange={e => setPhone(e.target.value)} />
          <input className={inputCls} placeholder={t('book.cb.ph_name')} value={name} onChange={e => setName(e.target.value)} />
          {err && <div className="text-xs text-rose-600">{t(err)}</div>}
          <button type="button" onClick={send} disabled={sending || phone.replace(/\D/g, '').length < 8}
            className="w-full py-2 rounded-xl bg-[#1c1a16] text-white text-sm font-medium disabled:opacity-50">{sending ? t('book.sending') : t('book.cb.send')}</button>
        </div>
      )}
    </div>
  );
}

export default function SaleBookingPanel({ roomId, info, contactPhone, searchRange }: Readonly<{ roomId: string; info: SaleBookingInfo; contactPhone?: string | null; searchRange?: { from: string; to: string } | null }>) {
  const { toast } = useToast();
  const { t, intl, fd } = useSaleLang();
  const [stage, setStage] = useState<Stage>('closed');
  // Điền sẵn từ những gì khách ĐÃ NHẬP trước đó (lưu trên thiết bị) → ngày đã chọn lần trước, ngày khách lọc ở trang, rồi mới tới mặc định
  const [saved] = useState(() => readSaleGuest());
  const [from, setFrom] = useState(() => storedStayDates(saved)?.from ?? (searchRange && searchRange.from >= todayISO() ? searchRange.from : dateAddDays(todayISO(), 1)));
  const [to, setTo] = useState(() => storedStayDates(saved)?.to ?? (searchRange && searchRange.from >= todayISO() ? searchRange.to : dateAddDays(todayISO(), 2)));
  const [adults, setAdults] = useState(() => saved.adults ?? 2);
  const [children, setChildren] = useState(() => saved.children ?? 0);
  const [quote, setQuote] = useState<Quote | null>(null);
  const eff: SaleBookingInfo = quote?.booking ?? info; // cấu hình theo TOÀ của căn (catalog chỉ có mặc định chung)
  const [quoteErr, setQuoteErr] = useState(''); // key từ điển (book.err.*)
  const [quoteErrN, setQuoteErrN] = useState<string>(''); // {n} chèn vào thông báo (số đêm/khách tối đa…)
  const [maxAdults, setMaxAdults] = useState(6);
  const [guestCapNote, setGuestCapNote] = useState(0); // 0 = không có; >0 = sức chứa tối đa vừa tự chỉnh về
  const [quoting, setQuoting] = useState(false);
  const [guest, setGuest] = useState(() => ({ name: saved.name ?? '', phone: saved.phone ?? '', email: saved.email ?? '', nationality: saved.nationality ?? '', note: '' }));
  const prefilled = !!(saved.name || saved.phone || saved.email || saved.nationality);
  const [hp, setHp] = useState(''); // honeypot chống bot — người thật không thấy/không điền
  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState(''); // key từ điển
  const [formErrN, setFormErrN] = useState('');
  const [payment, setPayment] = useState<Payment | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now()); // đồng hồ đếm ngược giữ chỗ ở màn thanh toán
  const [claim, setClaim] = useState<'idle' | 'sending' | 'sent' | 'unsent'>('idle'); // VietQR: khách báo đã chuyển khoản
  const [bookingId, setBookingId] = useState('');
  const accessKey = useRef('');
  const sessionId = useRef('');
  const stageRef = useRef<Stage>('closed');
  const formTracked = useRef(false);
  const pollErrLogged = useRef(false);
  useEffect(() => { stageRef.current = stage; }, [stage]);
  const quoting_ = stage === 'dates' || stage === 'form'; // chỉ báo giá khi đang ở bước chọn ngày/điền form

  const getSession = useCallback(() => {
    if (!sessionId.current) sessionId.current = (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return sessionId.current;
  }, []);

  const track = useCallback((event: string, step?: string, beacon = false) => {
    const body = JSON.stringify({ sessionId: getSession(), events: [{ event, roomId, step }] });
    try {
      if (beacon && navigator.sendBeacon) navigator.sendBeacon('/api/sale/track', new Blob([body], { type: 'application/json' }));
      else {
        fetch('/api/sale/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true })
          .catch(e => logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_track', message: e instanceof Error ? e.message : String(e) })); // log phụ: lỗi chỉ ghi nhận, không ảnh hưởng đặt phòng
      }
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_track', message: e instanceof Error ? e.message : String(e) });
    }
  }, [roomId, getSession]);

  // Bỏ dở: đóng modal/chuyển trang khi đang giữa các bước (chưa thanh toán xong)
  useEffect(() => {
    const abandon = () => {
      const s = stageRef.current;
      if (s === 'dates' || s === 'form' || s === 'payment') track('abandon', s, true);
    };
    window.addEventListener('pagehide', abandon);
    return () => { window.removeEventListener('pagehide', abandon); abandon(); };
  }, [track]);

  const open = () => { setStage('dates'); track('open'); };

  // Báo giá tự động khi đổi ngày/số khách (debounce)
  useEffect(() => {
    if (!quoting_) return;
    if (!from || !to || to <= from) { setQuote(null); setQuoteErr('book.err.invalid_dates'); setQuoteErrN(''); return; }
    let cancelled = false;
    setQuoting(true);
    const t = setTimeout(() => {
      fetch('/api/sale/book/quote', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ roomId, from, to, adults, children }) })
        .then(r => r.json().then(j => ({ status: r.status, j })))
        .then(({ status, j }) => {
          if (cancelled) return;
          if (status >= 500 || (status >= 400 && !j.error)) throw new Error(`quote ${status}`);
          if (j.ok) {
            setQuote(j); setQuoteErr(''); setQuoteErrN(''); setMaxAdults(j.maxGuests ?? 6);
            if (stageRef.current === 'dates') setStage('form');
            track('dates');
          } else if (j.error === 'too_many_guests' && Number.isInteger(j.detail) && j.detail >= 1) {
            // Số khách vượt sức chứa → tự đưa về mức tối đa để form thanh toán hiện ra ngay, khách không phải tự dò lỗi
            setMaxAdults(j.detail);
            setGuestCapNote(j.detail);
            setAdults(j.detail);
          } else {
            setQuote(null);
            setQuoteErr(errKey(j.error, 'book.err.quote_default')); setQuoteErrN(String(j.detail ?? ''));
            if (j.error === 'unavailable') track('unavailable');
            if (stageRef.current === 'form') setStage('dates');
          }
        })
        .catch(e => {
          if (cancelled) return;
          logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_quote', message: e instanceof Error ? e.message : String(e) });
          setQuote(null); setQuoteErr('book.err.quote_net'); setQuoteErrN('');
        })
        .finally(() => { if (!cancelled) setQuoting(false); });
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [from, to, adults, children, roomId, quoting_]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nhớ lại mọi thứ khách vừa nhập để các nơi khác (Gọi lại, Đặt lịch xem căn, lần mở sau) dùng lại — ghi chú tự do thì không lưu
  useEffect(() => {
    if (stage === 'closed') return;
    saveSaleGuest({ name: guest.name, phone: guest.phone, email: guest.email, nationality: guest.nationality, from, to, adults, children });
  }, [stage, guest.name, guest.phone, guest.email, guest.nationality, from, to, adults, children]);

  const forgetMe = () => {
    clearSaleGuest();
    setGuest({ name: '', phone: '', email: '', nationality: '', note: guest.note });
    toast(t('book.forgot'));
  };

  const touchForm = () => { if (!formTracked.current) { formTracked.current = true; track('form'); } };

  const submit = async () => {
    if (submitting || !quote) return;
    setSubmitting(true); setFormErr(''); setFormErrN('');
    try {
      const res = await fetch('/api/sale/book/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, from, to, adults, children, sessionId: sessionId.current || undefined, hp, guest }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.ok) {
        if (res.status >= 500 || !j.error) throw new Error(`create ${res.status}`);
        setFormErr(errKey(j.error, 'book.err.create_default')); setFormErrN(String(j.detail ?? ''));
        if (j.error === 'unavailable') setStage('dates');
        return;
      }
      setBookingId(j.bookingId);
      if (!j.payment) { setStage('held'); return; } // đã giữ chỗ nhưng chưa sinh được QR → NV liên hệ
      accessKey.current = j.accessKey;
      setPayment(j.payment); setStage('payment');
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_create', message: e instanceof Error ? e.message : String(e) });
      setFormErr('book.err.create_net');
    } finally { setSubmitting(false); }
  };

  useEffect(() => {
    if (stage !== 'payment') return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [stage]);

  // Poll trạng thái thanh toán
  useEffect(() => {
    if (stage !== 'payment' || !bookingId) return;
    const id = setInterval(() => {
      fetch(`/api/sale/book/status?bookingId=${encodeURIComponent(bookingId)}&key=${encodeURIComponent(accessKey.current)}`)
        .then(r => (r.ok ? r.json() : null))
        .then(j => { if (j?.status === 'paid') setStage('paid'); else if (j?.status === 'conflict') setStage('conflict'); })
        .catch(e => {
          // Poll lỗi mạng tạm thời: lần sau thử lại, không báo khách — nhưng ghi nhận 1 lần/phiên để thấy nếu lỗi kéo dài
          if (pollErrLogged.current) return;
          pollErrLogged.current = true;
          logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_poll', message: e instanceof Error ? e.message : String(e) });
        });
    }, 5000);
    return () => clearInterval(id);
  }, [stage, bookingId]);

  const callback = (
    <CallbackBox roomId={roomId} getSession={getSession} contactPhone={contactPhone}
      defaults={{ phone: guest.phone, name: guest.name, from, to, adults, bookingId: bookingId || undefined }} />
  );

  // VietQR không tự xác nhận được → khách bấm "Tôi đã chuyển khoản" để NV kiểm tra sao kê ngay (không phải chờ NV tự để ý)
  const claimTransfer = async () => {
    if (claim === 'sending' || claim === 'sent') return;
    setClaim('sending');
    try {
      const res = await fetch('/api/sale/book/transferred', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingId, key: accessKey.current, sessionId: getSession() }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(`transferred ${res.status}`);
      setClaim(j.notified ? 'sent' : 'unsent');
    } catch (e) {
      logPublicClientError({ route: 'app/sale/page.tsx', action: 'sale_book_transferred', message: e instanceof Error ? e.message : String(e) });
      setClaim('idle');
      toast(t('book.claim.err'), 'error');
    }
  };

  const copy = (text: string, label: string) => navigator.clipboard.writeText(text).then(() => toast(t('book.copied', { label })), () => toast(t('book.copyfail'), 'error'));
  const holdText = holdLabel(t, eff.holdMinutes);
  const phoneLink = contactPhone ? <> <a className="underline font-semibold" href={`tel:${contactPhone.replace(/\s/g, '')}`}>{contactPhone}</a></> : null;

  if (stage === 'closed') {
    return (
      <div className="space-y-2">
        <button type="button" onClick={open} className="w-full text-left rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3">
          <div className="text-sm font-bold">{t('book.open.title')}</div>
          <div className="text-xs opacity-90">
            {eff.discountEnabled ? t('book.open.sub_disc', { pct: eff.discountPercent }) : ''}{t('book.open.sub')}
          </div>
        </button>
        {callback}
      </div>
    );
  }

  const inputCls = 'w-full border border-[#e4ddd4] rounded-lg px-2 py-1.5 text-sm';
  const holdUntil = payment ? new Date(payment.expiresAtMs).toLocaleString(intl, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '';

  if (stage === 'paid') {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-1 text-sm text-gray-800">
        <div className="font-bold text-emerald-800">{t('book.paid.title')}</div>
        <div>{t('book.code')} <b style={{ fontFamily: 'ui-monospace, monospace' }}>{bookingId}</b></div>
        <div className="text-emerald-800">{t('book.paid.body', { from: fd(from), to: fd(to) })}</div>
      </div>
    );
  }
  if (stage === 'conflict') {
    return (
      <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 space-y-1 text-sm text-gray-800">
        <div className="font-bold text-rose-700">{t('book.conflict.title')}</div>
        <div><Rich text={t('book.conflict.body', { id: bookingId })} /></div>
        {callback}
      </div>
    );
  }
  if (stage === 'held') {
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 space-y-1 text-sm text-gray-800">
        <div className="font-bold text-amber-800">{t('book.held.title')}</div>
        <div><Rich text={t('book.held.body', { id: bookingId, hold: holdText })} /></div>
        {callback}
      </div>
    );
  }

  if (stage === 'payment' && payment) {
    const isTransfer = payment.mode === 'vietqr';
    const remaining = payment.expiresAtMs - nowMs;
    if (remaining <= 0) {
      // Hết thời gian giữ chỗ: ẩn QR (link PayOS cũng đã hết hạn) — nếu khách ĐÃ chuyển khoản thì poll vẫn bắt được và đổi sang màn "đã thanh toán"
      return (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2 text-sm text-gray-800">
          <div className="font-bold text-amber-800">{t('book.expired.title', { hold: holdText })}</div>
          <div><Rich text={t('book.expired.body', { id: bookingId })} slots={{ phone: phoneLink }} /></div>
          <button type="button" onClick={() => { setPayment(null); setBookingId(''); setClaim('idle'); setStage('dates'); }}
            className="w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">{t('book.rebook')}</button>
          {callback}
        </div>
      );
    }
    const mm = String(Math.floor(remaining / 60_000)).padStart(2, '0'), ss = String(Math.floor((remaining % 60_000) / 1000)).padStart(2, '0');
    return (
      <div className="rounded-xl border border-emerald-300 p-4 space-y-3">
        <div className="text-sm font-bold">{t('book.pay.title')} <span className={`ml-1 font-mono text-xs ${remaining < 5 * 60_000 ? 'text-rose-600' : 'text-[#8a7a68]'}`}>{t('book.pay.left', { time: `${mm}:${ss}` })}</span></div>
        {payment.qrImage
          // eslint-disable-next-line @next/next/no-img-element
          ? <img src={payment.qrImage} alt={t('book.qr.alt')} className="w-52 h-52 mx-auto rounded-xl bg-white p-2" />
          : payment.qrPayload ? <QrSvg value={payment.qrPayload} /> : null}
        <div className="text-center">
          <div className="text-2xl font-bold text-emerald-700">{fmtVND(payment.amountDue)}</div>
          {payment.amountDue < payment.totalAmount && <div className="text-xs text-[#8a7a68]">{t('book.pay.deposit', { total: fmtVND(payment.totalAmount) })}</div>}
        </div>
        {isTransfer && payment.bank && (
          <div className="text-xs space-y-1 bg-[#f7f4ee] rounded-lg p-3">
            <div>{t('book.pay.bank')} <b>{payment.bank.bankName}</b></div>
            <div className="flex items-center gap-2">{t('book.pay.acc_no')} <b>{payment.bank.accountNo}</b> <button type="button" className="underline text-[#8a7a68]" onClick={() => copy(payment.bank!.accountNo ?? '', t('book.copy_account'))}>{t('book.pay.copy')}</button></div>
            <div>{t('book.pay.acc_name')} <b>{payment.bank.accountName}</b></div>
          </div>
        )}
        <HoldNotice hold={holdText} until={holdUntil} contactPhone={contactPhone} transfer={isTransfer} />
        <div className="text-xs bg-[#f7f4ee] rounded-lg p-3 space-y-1">
          <div className="flex items-center gap-2">{t('book.pay.content')} <b style={{ fontFamily: 'ui-monospace, monospace' }}>{payment.transferContent}</b>
            <button type="button" className="underline text-[#8a7a68]" onClick={() => copy(payment.transferContent, t('book.copy_content'))}>{t('book.pay.copy')}</button></div>
          <div>{t('book.code')} <b>{bookingId}</b></div>
        </div>
        {isTransfer && (claim === 'sent' || claim === 'unsent'
          ? <div className="text-xs rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-emerald-800">
              {claim === 'sent' ? t('book.claim.sent') : t('book.claim.unsent')}
            </div>
          : <button type="button" onClick={claimTransfer} disabled={claim === 'sending'}
              className="w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
              {claim === 'sending' ? t('book.sending') : t('book.claim.btn')}
            </button>)}
        {payment.checkoutUrl && <a href={payment.checkoutUrl} target="_blank" rel="noreferrer" className="block text-center text-sm underline text-emerald-700">{t('book.pay.open_page')}</a>}
        {callback}
      </div>
    );
  }

  const nights = quote?.nightCount ?? 0;
  return (
    <div className="rounded-xl border border-emerald-300 p-4 space-y-3">
      <div className="text-sm font-bold">{t('book.title')}</div>
      <StayDatePicker roomId={roomId} from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-[#8a7a68]">{t('book.adults')}
          <select className={inputCls} value={adults} onChange={e => { setGuestCapNote(0); setAdults(Number(e.target.value)); }}>{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].filter(n => n <= maxAdults || n === adults).map(n => <option key={n} value={n}>{n}</option>)}</select>
        </label>
        <label className="text-xs text-[#8a7a68]">{t('book.children')}
          <select className={inputCls} value={children} onChange={e => setChildren(Number(e.target.value))}>{[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}</select>
        </label>
      </div>
      {guestCapNote > 0 && <div className="text-xs text-amber-700">{t('book.guestcap', { n: guestCapNote })}</div>}
      {quote && quote.extraGuests > 0 && !quoteErr && (
        <div className="text-xs text-amber-700">{t('book.extra_note', { max: quote.maxAdults, next: quote.maxAdults + 1, fee: fmtVND(quote.extraGuestFeePerNight) })}</div>
      )}
      {quote && quote.extraGuestFeePerNight > 0 && quote.extraGuests === 0 && quote.maxGuests > quote.maxAdults && !quoteErr && (
        <div className="text-[11px] text-[#8a7a68]">{t('book.extra_hint', { max: quote.maxAdults, more: quote.maxGuests - quote.maxAdults, fee: fmtVND(quote.extraGuestFeePerNight) })}</div>
      )}
      {quoting && <div className="text-xs text-[#8a7a68]">{t('book.checking')}</div>}
      {quoteErr && !quoting && <div className="text-xs text-rose-600">{t(quoteErr, { n: quoteErrN })}</div>}

      {quote && !quoteErr && (
        <div className="text-sm space-y-1 bg-[#f7f4ee] rounded-lg p-3">
          {quote.listTotal > 0 ? (
            <div className="flex justify-between text-[#8a7a68]"><span>{t('book.list_price', { n: nights })}</span><span className="line-through">{fmtVND(quote.listTotal)}</span></div>
          ) : (
            <div className="flex justify-between"><span>{t('book.nights', { n: nights })}</span><span>{fmtVND(quote.subtotal)}</span></div>
          )}
          {quote.discount > 0 && <div className="flex justify-between text-emerald-700"><span>{t('book.discount', { pct: quote.discountPercent })}</span><span>−{fmtVND(quote.discount)}</span></div>}
          {quote.extraGuestFee > 0 && (
            <div className="flex justify-between text-[#4a3d30]">
              <span>{t('book.extra_fee', { n: quote.extraGuests, fee: fmtVND(quote.extraGuestFeePerNight), nights })}</span>
              <span>+{fmtVND(quote.extraGuestFee)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold border-t border-[#e4ddd4] pt-1"><span>{quote.listTotal > 0 ? t('book.direct_price') : t('book.total')}</span><span>{fmtVND(quote.total)}</span></div>
          {quote.due < quote.total && <div className="flex justify-between text-amber-700 text-xs"><span>{t('book.deposit_now', { pct: eff.depositPercent })}</span><span>{fmtVND(quote.due)}</span></div>}
        </div>
      )}

      {stage === 'form' && quote && (
        <div className="space-y-2">
          {prefilled && (
            <div className="text-[11px] text-[#8a7a68]">
              {t('book.prefilled')} <button type="button" className="underline" onClick={forgetMe}>{t('book.forget_btn')}</button>
            </div>
          )}
          <input tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={hp} onChange={e => setHp(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
          <input className={inputCls} placeholder={t('book.ph_name')} value={guest.name} onChange={e => { touchForm(); setGuest(g => ({ ...g, name: e.target.value })); }} />
          <input className={inputCls} inputMode="tel" placeholder={t('book.cb.ph_phone')} value={guest.phone} onChange={e => { touchForm(); setGuest(g => ({ ...g, phone: e.target.value })); }} />
          <input className={inputCls} inputMode="email" placeholder={t('book.ph_email')} value={guest.email} onChange={e => { touchForm(); setGuest(g => ({ ...g, email: e.target.value })); }} />
          <input className={inputCls} placeholder={t('book.ph_nat')} value={guest.nationality} onChange={e => { touchForm(); setGuest(g => ({ ...g, nationality: e.target.value })); }} />
          <textarea className={inputCls} rows={2} maxLength={500} placeholder={t('book.ph_note')} value={guest.note} onChange={e => { touchForm(); setGuest(g => ({ ...g, note: e.target.value })); }} />
          {eff.note && <div className="text-[11px] text-[#8a7a68] whitespace-pre-line">{eff.note}</div>}
          {formErr && <div className="text-xs text-rose-600">{t(formErr, { n: formErrN })}</div>}
          <HoldNotice hold={holdText} contactPhone={contactPhone} transfer={(quote?.payMode ?? eff.payMode) === 'vietqr'} />
          <button type="button" onClick={submit} disabled={submitting || !guest.name.trim() || !guest.phone.trim()}
            className="w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
            {submitting ? t('book.creating') : t('book.submit', { amount: fmtVND(quote.due) })}
          </button>
        </div>
      )}
      {callback}
    </div>
  );
}
